import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { MultimediaCategory } from "../../types";
import { Chip, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MultimediaCategoryModal from "../../components/ScreenCategories/MultimediaCategoryModal";
import Popover from "@mui/material/Popover";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import PageHeader from "../../components/common/PageHeader";
import { useStores } from "../../hooks/useStores";
import { observer } from "mobx-react";
import { useSnackbar } from "notistack";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import EmptySpace from "../../components/common/EmptySpace";
import Loading from "../../components/common/Loading";

const MultimediaCategories: React.FC = observer(() => {
  const { t } = useTranslation();
  const { categoryStore } = useStores();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const modalHandler = () => {
    setIsModalOpen((prev) => !prev);
  };

  const [multimediaCategory, setMultimediaCategory] =
    useState<MultimediaCategory>();

  useEffect(() => {
    categoryStore.getMultimediaCategories();
  }, [categoryStore]);

  const handleSubmit = useCallback(
    async (data: MultimediaCategory) => {
      data.visibleInApp === "visible"
        ? (data.visibleInApp = true)
        : (data.visibleInApp = false);

      try {
        await categoryStore.addMultimediaCategory(data);
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }

      modalHandler();
    },
    [categoryStore, enqueueSnackbar, t]
  );

  const handleUpdate = useCallback(
    async (data: MultimediaCategory) => {
      data.visibleInApp === "visible"
        ? (data.visibleInApp = true)
        : (data.visibleInApp = false);

      try {
        await categoryStore.updateMultimediaCategory(data);
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      setMultimediaCategory(undefined);
      modalHandler();
    },
    [categoryStore, enqueueSnackbar, t]
  );

  const handleMultimediaCategoriesOrderUpdate = useCallback(
    async (
      category: MultimediaCategory,
      oldIndex: number,
      newIndex: number
    ) => {
      try {
        await categoryStore.updateMultimediaCategoriesOrder(
          category,
          oldIndex,
          newIndex
        );

        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      await categoryStore.getMultimediaCategories();
    },
    [categoryStore, enqueueSnackbar, t]
  );

  const handleDelete = useCallback(
    async (data: MultimediaCategory) => {
      try {
        await categoryStore.deleteMultimediaCategory(data);
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
    },
    [categoryStore, enqueueSnackbar, t]
  );

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.programCategories.column.order"),
        dataKey: "orderNumber",
        numeric: true,
        sorter: (a: number, b: number) => (a < b ? -1 : a > b ? 1 : 0),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.multimediaCategories.column.name"),
        dataKey: "name",
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.multimediaCategories.column.visible"),
        dataKey: "visibleInApp",
        render: (visibleInApp: boolean | string) => {
          return visibleInApp === true ? (
            <Chip
              label={t("screens.multimediaCategories.column.visibleActive")}
              color="success"
            />
          ) : (
            <Chip
              label={t("screens.multimediaCategories.column.visibleNotActive")}
              color="error"
            />
          );
        },
      },
      {
        width: 90,
        flexGrow: 0,
        label: t("common.action"),
        dataKey: "id",
        render: (id: number, category: MultimediaCategory) => {
          return (
            <>
              <IconButton
                key={id}
                onClick={() => {
                  setMultimediaCategory(category);
                  modalHandler();
                }}
              >
                <EditIcon />
              </IconButton>

              <PopupState variant="popover" popupId="demo-popup-popover">
                {(popupState) => (
                  <div>
                    <IconButton color="error" {...bindTrigger(popupState)}>
                      <DeleteIcon />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("common.deletePopUp")}
                        <IconButton
                          color="error"
                          sx={{ p: 0 }}
                          onClick={() => handleDelete(category)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Typography>
                    </Popover>
                  </div>
                )}
              </PopupState>
            </>
          );
        },
      },
    ],
    [handleDelete, t]
  );

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.multimediaCategories.breadcrumb.home")}
          </BreadcrumbItem>

          <BreadcrumbItem active>
            {t("screens.multimediaCategories.breadcrumb.multimediaCategories")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.multimediaCategories.pageHeader.title")}
          buttonText={t("screens.multimediaCategories.pageHeader.buttonText")}
          buttonEnable={true}
          handleButton={modalHandler}
        />
      </Content>
      {categoryStore.isLoading ? (
        <Loading />
      ) : categoryStore.multimediaCategories.length > 0 ? (
        <Content fullHeight align="top" mt={[null, null, null, "0px"]}>
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${
                (categoryStore.multimediaCategories.length + 1) * 50
              }px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              rowCount={categoryStore.multimediaCategories.length}
              rowGetter={({ index }) =>
                categoryStore.multimediaCategories[index]
              }
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
              data={categoryStore.multimediaCategories}
              onRowSort={handleMultimediaCategoriesOrderUpdate}
            />
          </div>
        </Content>
      ) : (
        <EmptySpace
          title={t("screens.multimediaCategories.emptyData")}
          buttonEnable={true}
          buttonText={t("screens.multimediaCategories.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      )}

      <MultimediaCategoryModal
        category={multimediaCategory}
        isSubmitting={false}
        isOpen={isModalOpen}
        defaultOrderNumber={categoryStore.multimediaCategories.length + 1}
        onClose={() => {
          setMultimediaCategory(undefined);
          modalHandler();
        }}
        onSubmit={multimediaCategory ? handleUpdate : handleSubmit}
      />
    </Layout>
  );
});

export default MultimediaCategories;
